import React, {
  FormEvent,
  useEffect,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Rating,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import {
  Link,
  useHistory,
  useLocation,
} from 'react-router-dom';

import {
  AccountTable,
  ApplicantsBoard,
  DashboardError,
  DashboardPage,
  IAccountTableColumn,
} from '../../components/JobsAccount';
import { book } from '../../routers/books';
import { useGetJobsAccountApplicantsQuery } from '../../services/serviceJobsAccount';
import { useUpdateApplicationStatusMutation } from '../../services/serviceJobsAccount';
import {
  IJobsAccountApplicant,
  IJobsAccountApplicantsRequest,
} from '../../services/serviceJobsAccount/interfaces';
import { NoAccessMessage } from '../../components/NoAccessMessage';
import { isForbidden } from '../../utils/jobAccessError';

const PAGE_SIZE = 25;

const formatDate = (date: string | null): string => (
  date ? moment(date).format('MM/DD/YYYY') : '—'
);

const initialRequest = (newThisWeekOnly: boolean): IJobsAccountApplicantsRequest => ({
  page: 1,
  pageSize: PAGE_SIZE,
  sortBy: 'DateSent',
  sortDirection: 'desc',
  newThisWeekOnly,
});

export const JobsAccountApplicants = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const newThisWeekOnly = pathname === book.jobsNewApplicants;
  const [request, setRequest] = useState<IJobsAccountApplicantsRequest>(
    initialRequest(newThisWeekOnly)
  );
  const [searchInput, setSearchInput] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [view, setView] = useState<'board' | 'list'>('list');
  const [updateStatus, updateStatusResult] = useUpdateApplicationStatusMutation();

  useEffect(() => {
    setRequest(initialRequest(newThisWeekOnly));
    setSearchInput('');
  }, [newThisWeekOnly]);

  const { data, error, isLoading, isFetching } = useGetJobsAccountApplicantsQuery(request);
  const applicants = data?.result || [];
  const newApplicantCount = data?.newApplicantCount || 0;
  const statuses = data?.statuses || [];
  const effectiveView = isMobile ? 'list' : view;

  const openApplicant = (application: IJobsAccountApplicant) => history.push(
    book.jobsApplicantDetail(application.jobGuid, application.applicationId),
    { fromApplicantsPath: pathname }
  );

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setRequest((current) => ({
      ...current,
      page: 1,
      searchText: searchInput.trim() || undefined,
    }));
  };

  const clearSearch = () => {
    setSearchInput('');
    setRequest((current) => ({
      ...current,
      page: 1,
      searchText: undefined,
    }));
  };

  const columns: IAccountTableColumn<IJobsAccountApplicant>[] = [
    {
      id: 'applicant',
      label: 'Applicant',
      sortBy: 'Applicant',
      render: (application) => (
        <Box>
          <Button
            variant="text"
          onClick={() => history.push(
              book.jobsApplicantDetail(
                application.jobGuid,
                application.applicationId
              ),
              { fromApplicantsPath: pathname }
            )}
            sx={{
              fontWeight: application.isRead ? 600 : 800,
              p: 0,
              textAlign: 'left',
              textTransform: 'none',
            }}
          >
            {application.name || application.email || 'Applicant'}
          </Button>
          {application.name && application.email ? (
            <Typography color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.25 }}>
              {application.email}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      id: 'stage',
      label: 'Stage',
      width: 170,
      render: (application) => (
        <TextField
          select
          value={application.statusId}
          size="small"
          disabled={updateStatusResult.isLoading}
          inputProps={{ 'aria-label': `Stage for ${application.name || 'applicant'}` }}
          onChange={(event) => updateStatus({
            eguid: application.jobGuid,
            applicationId: application.applicationId,
            statusId: Number(event.target.value),
          })}
          sx={{ minWidth: 145 }}
        >
          {statuses.map((status) => (
            <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      id: 'rating',
      label: 'Rating',
      width: 150,
      render: (application) => <Rating value={application.rating || 0} size="small" readOnly />,
    },
    {
      id: 'jobTitle',
      label: 'Job',
      sortBy: 'JobTitle',
      render: (application) => (
        <Box>
          <Box
            component={Link}
            to={book.jobsApplicants(application.jobGuid)}
            sx={{
              color: 'var(--ink)',
              fontWeight: 700,
              textDecoration: 'none',
              '&:hover': { color: 'var(--accent-text)' },
            }}
          >
            {application.jobTitle || 'Job listing'}
          </Box>
          {!application.isJobActive ? (
            <Typography
              sx={{
                color: 'var(--ink-3)',
                fontSize: '0.8rem',
                mt: 0.35,
              }}
            >
              Inactive listing
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      id: 'dateSent',
      label: 'Date Applied',
      sortBy: 'DateSent',
      width: 140,
      render: (application) => formatDate(application.dateSent),
    },
    {
      id: 'appliedVia',
      label: 'Applied Via',
      width: 130,
      render: (application) => application.appliedVia || 'CPG Jobs',
    },
    {
      id: 'status',
      label: 'Status',
      sortBy: 'IsRead',
      width: 120,
      render: (application) => (
        <Chip
          label={application.isRead ? 'Viewed' : 'New'}
          size="small"
          color={application.isRead ? 'default' : 'primary'}
          sx={{
            borderRadius: 0,
            fontSize: '0.8rem',
            fontWeight: 700,
          }}
        />
      ),
    },
  ];

  return (
    <DashboardPage
      title={newThisWeekOnly ? 'New Applicants' : 'All Applicants'}
      noAccessComponent={isForbidden(error) ? <NoAccessMessage /> : undefined}
    >
      <Tabs
        value={newThisWeekOnly ? 'new' : 'all'}
        sx={{ mb: 3 }}
        aria-label="Applicant date scope"
      >
        <Tab
          component={Link}
          to={book.jobsNewApplicants}
          aria-label={newApplicantCount > 0
            ? `New This Week, ${newApplicantCount} applicants`
            : 'New This Week'}
          label={(
            <Stack
              component="span"
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Box component="span">New This Week</Box>
              {newApplicantCount > 0 ? (
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    alignItems: 'center',
                    backgroundColor: 'var(--accent)',
                    color: '#fff',
                    display: 'inline-flex',
                    fontSize: '0.8rem',
                    fontWeight: 800,
                    height: 22,
                    justifyContent: 'center',
                    minWidth: 22,
                    px: 0.75,
                  }}
                >
                  {newApplicantCount}
                </Box>
              ) : null}
            </Stack>
          )}
          value="new"
        />
        <Tab
          component={Link}
          to={book.jobsAllApplicants}
          label="All Applicants"
          value="all"
        />
      </Tabs>

      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Search and review applicants across every job listing you manage.
      </Typography>

      <ToggleButtonGroup
        exclusive
        value={effectiveView}
        onChange={(_event, value) => value && setView(value)}
        size="small"
        aria-label="Applicant view"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="board" disabled={isMobile}>Board</ToggleButton>
        <ToggleButton value="list">List</ToggleButton>
      </ToggleButtonGroup>

      <Stack
        component="form"
        onSubmit={submitSearch}
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <TextField
          label="Search applicants"
          placeholder="Name, email, or job title"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: { md: 260 } }}
        />
        <TextField
          select
          label="Job listing"
          value={request.jobGuid || ''}
          onChange={(event) => setRequest((current) => ({
            ...current,
            jobGuid: event.target.value || undefined,
            page: 1,
          }))}
          size="small"
          sx={{ minWidth: { md: 240 } }}
        >
          <MenuItem value="">All job listings</MenuItem>
          {(data?.jobs || []).map((job) => (
            <MenuItem key={job.jobGuid} value={job.jobGuid}>
              {job.jobTitle || 'Job listing'} ({job.applicantCount})
              {job.isActive ? '' : ' · inactive'}
            </MenuItem>
          ))}
        </TextField>
        {effectiveView === 'list' ? (
          <TextField
            select
            label="Stage"
            value={request.statusId || ''}
            onChange={(event) => setRequest((current) => ({
              ...current,
              statusId: event.target.value ? Number(event.target.value) : undefined,
              page: 1,
            }))}
            size="small"
            sx={{ minWidth: { md: 150 } }}
          >
            <MenuItem value="">All stages</MenuItem>
            {statuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.name} ({status.applicantCount})
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField
          select
          label="Status"
          value={request.isRead === undefined ? '' : String(request.isRead)}
          onChange={(event) => setRequest((current) => ({
            ...current,
            isRead: event.target.value === ''
              ? undefined
              : event.target.value === 'true',
            page: 1,
          }))}
          size="small"
          sx={{ minWidth: { md: 150 } }}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="false">New</MenuItem>
          <MenuItem value="true">Viewed</MenuItem>
        </TextField>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SearchIcon />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Search
        </Button>
        {request.searchText ? (
          <Button variant="text" onClick={clearSearch}>
            Clear
          </Button>
        ) : null}
      </Stack>

      {request.searchText ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Results for “{request.searchText}”
        </Typography>
      ) : null}

      <DashboardError
        error={error}
        fallback="We could not load your applicants. Please try again."
      />
      {effectiveView === 'board' ? (
        <ApplicantsBoard
          statuses={statuses}
          request={{ ...request, statusId: undefined }}
          onOpen={openApplicant}
        />
      ) : (
      <AccountTable
        ariaLabel={newThisWeekOnly ? 'New applicants this week' : 'All applicants'}
        columns={columns}
        rows={applicants}
        getRowId={(application) => application.applicationId}
        emptyMessage={newThisWeekOnly
          ? 'There are no new applicants from the last 7 days.'
          : 'No applicants match these filters.'}
        isLoading={isLoading || isFetching}
        page={request.page}
        pageSize={request.pageSize}
        total={data?.total || 0}
        sortBy={request.sortBy}
        sortDirection={request.sortDirection}
        onPageChange={(page) => setRequest((current) => ({ ...current, page }))}
        onSort={(sortBy, sortDirection) => setRequest((current) => ({
          ...current,
          page: 1,
          sortBy,
          sortDirection,
        }))}
      />
      )}
    </DashboardPage>
  );
};

export default JobsAccountApplicants;
